import { zip } from 'fflate'
import { FilterMatchMode } from 'primereact/api'
import { DataTable, DataTableFilterMeta } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  useGetAdmSequencePoints,
  useGetAdmSequencesForSeries,
  useGetAdmSequencesForTeam
} from '../api/adm-sequence-API'
import {
  useCreateAttitudeSet,
  useDeleteAttitudeSet,
  useGetAttitudeSets,
  useUpdateAttitudeSet,
  useUploadAttitudeSetCsv
} from '../api/attitude-set-API'
import { useGetAdmApiSeries } from '../api/pressure-tap-import-API'
import { useGetRaceTeam } from '../api/raceteam-API'
import {
  finalizeRHRepoSetStreaming,
  useCreateRHRepoSet,
  useGetRHRepoSets,
  useUpdateRHRepoSet
} from '../api/rhrepo-set-API'
import {
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  EyeIcon,
  LoadingIcon,
  RefreshIcon,
  SaveIcon,
  UploadIcon
} from '../components/Icons'
import { protoService } from '../proto/ProtoService'
import { currentUserState } from '../utils/state-atoms'
import { canMutate } from '../utils/roles'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { classNames } from 'primereact/utils'
import { InputText } from 'primereact/inputtext'
import { Column, ColumnBodyOptions } from 'primereact/column'
import { AttitudeState, RHRepoSet, RHRepo, CFDFile } from '@aero-platform/shared'
import { parseRHEncodingComponents, rhComponentMatchesValue } from '../utils/rh-encoding'
import IconButton from '../components/buttons/IconButton'
import AttitudeSetStateEditor from '../components/AttitudeSetStateEditor'
import { confirmDialog } from 'primereact/confirmdialog'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { useGenerateBlobSasUrlUpload, useGenerateBatchBlobSasUrlUpload } from '../api/upload-API'
import { uploadFileDirectToBlob } from '../services/blobUploadService'
import simUploadQueueStore from '../services/simUploadQueueStore'
import { AttitudeSet, LazyTableState } from '../types/types'
import { NASCAR28Teams, NASCARTeams, UploadTypes } from '../utils/constants'
import { attitudeSetListState, currentGMIDState, currentTeamState } from '../utils/state-atoms'
import { DropdownStyle } from '../utils/styling-constants'

const defaultAttitudeSetFilters: DataTableFilterMeta = {
  name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  methodId: { value: null, matchMode: FilterMatchMode.EQUALS },
  status: { value: null, matchMode: FilterMatchMode.EQUALS }
}

// Base state columns definition - will be filtered in component based on team
const baseStateColumns = [
  {
    header: 'Order Number',
    field: 'order_number',
    filter: true,
    headerStyle: {},
    bodyStyle: {},
    bodyClassName: 'id-cell max-w-[200px] w-[200px]',
    headerClassName: 'max-w-[200px]',
    sortable: true
  },
  {
    header: 'State Name',
    field: 'name',
    filter: false,
    headerStyle: {},
    bodyStyle: {},
    bodyClassName: 'max-w-[250px] w-[250px]',
    headerClassName: 'max-w-[250px]',
    sortable: false,
    editable: true
  },
  {
    header: 'Ride Height File',
    field: 'rideHeightFile',
    headerClassName: 'max-w-[300px]',
    bodyClassName: 'max-w-[300px] w-[250px]',
    isRideHeightColumn: true // Special flag to handle in render
  },
  {
    header: 'CCA ID',
    field: 'cca_id',
    headerClassName: 'max-w-[150px]',
    bodyClassName: 'max-w-[150px] w-[120px]',
    body: rowData => {
      return <span>{rowData.cca_id || ''}</span>
    }
  },
  {
    header: 'FRH',
    field: 'front_height',
    headerClassName: 'max-w-[150px]',
    bodyClassName: 'max-w-[150px] w-[80px]',
    body: rowData => {
      const num = rowData.front_height
      return <span>{num && num.toFixed(5)}</span>
    }
  },
  {
    header: 'RRH',
    field: 'rear_height',
    headerClassName: 'max-w-[150px]',
    bodyClassName: 'max-w-[150px] w-[80px]',
    body: rowData => {
      const num = rowData.rear_height
      return <span>{num && num.toFixed(5)}</span>
    }
  },
  {
    header: 'Steer',
    field: 'steer',
    headerClassName: 'max-w-[150px]',
    bodyClassName: 'max-w-[150px] w-[80px]',
    body: rowData => {
      const num = rowData.steer
      return <span>{num && num.toFixed(5)}</span>
    }
  },
  {
    header: 'Roll',
    field: 'roll',
    headerClassName: 'max-w-[150px]',
    bodyClassName: 'max-w-[150px] w-[80px]',
    body: rowData => {
      const num = rowData.roll
      return <span>{num && num.toFixed(1)}</span>
    }
  },
  {
    header: 'Front Flap',
    field: 'front_flap',
    headerClassName: 'max-w-[150px]',
    bodyClassName: 'max-w-[150px] w-[80px]',
    body: rowData => {
      const num = rowData.front_flap
      return <span>{num && num.toFixed(5)}</span>
    }
  },
  {
    header: 'Rear Flap',
    field: 'rear_flap',
    headerClassName: 'max-w-[150px]',
    bodyClassName: 'max-w-[150px] w-[80px]',
    body: rowData => {
      const num = rowData.rear_flap
      return <span>{num && num.toFixed(5)}</span>
    }
  },
  {
    header: 'Yaw',
    field: 'yaw',
    headerClassName: 'max-w-[150px]',
    bodyClassName: 'max-w-[150px] w-[80px]',
    body: rowData => {
      const num = rowData.yaw
      return <span>{num && num.toFixed(5)}</span>
    }
  },
  {
    header: 'Rad',
    field: 'radius',
    headerClassName: 'max-w-[150px]',
    bodyClassName: 'max-w-[150px] w-[80px]',
    body: rowData => {
      const num = rowData.radius
      return <span>{num && num.toFixed(5)}</span>
    }
  },
  {
    header: 'Spd',
    field: 'air_speed',
    headerStyle: { width: '64px', maxWidth: '64px', minWidth: '64px' },
    headerClassName: 'max-w-[64px] w-[64px]',
    bodyStyle: { width: '64px', maxWidth: '64px', minWidth: '64px' },
    bodyClassName: 'max-w-[64px] w-[64px]',
    body: rowData => {
      const num = rowData.air_speed
      return <span>{num && num.toFixed(5)}</span>
    }
  },
  {
    header: 'Exh',
    field: 'exhaust',
    headerClassName: 'max-w-[150px]',
    bodyClassName: 'max-w-[150px] w-[80px]',
    body: rowData => {
      const num = rowData.exhaust
      return <span>{num && num.toFixed(5)}</span>
    }
  },
  {
    header: 'SPEC',
    field: 'spec',
    headerClassName: 'max-w-[150px]',
    bodyClassName: 'max-w-[150px] w-[80px]',
    nascar28Only: true,
    body: rowData => {
      const num = rowData.spec
      return <span>{num != null ? num.toFixed(1) : ''}</span>
    }
  },
  {
    header: 'RAD_BLOCKER',
    field: 'rad_blocker',
    headerClassName: 'max-w-[150px]',
    bodyClassName: 'max-w-[150px] w-[80px]',
    nascar28Only: true,
    body: rowData => {
      const num = rowData.rad_blocker
      return <span>{num != null ? num.toFixed(1) : ''}</span>
    }
  }
]

const setColumns = [
  {
    header: 'Name',
    field: 'name',
    filter: true,
    headerStyle: {},
    bodyStyle: {},
    bodyClassName: 'id-cell max-w-[150px] w-[80px]',
    headerClassName: 'max-w-[150px]',
    sortable: true
  },
  {
    header: 'Description',
    field: 'description',
    headerClassName: 'max-w-[300px]',
    bodyClassName: 'max-w-[300px] w-[150px]'
  },
  {
    header: 'State Count',
    headerClassName: 'max-w-[150px]',
    bodyClassName: 'max-w-[150px] w-[80px]',
    field: 'attitudeStates.length'
  }
]

const speedTypeOptions = [
  { label: 'MPH', value: 'MPH' },
  { label: 'KPH', value: 'KPH' },
  { label: 'm/s', value: 'm/s' }
]

const renderSpeedTypeHeader = (
  value: string,
  onChange: (value: string) => void,
  disabled = false
) => (
  <div className="relative inline-block w-[56px] mt-1 mb-1">
    <Dropdown
      value={value || null}
      options={speedTypeOptions}
      optionLabel="label"
      optionValue="value"
      placeholder="Spd"
      disabled={disabled}
      pt={{
        ...DropdownStyle,
        root: {
          ...DropdownStyle.root,
          className: classNames(
            '!min-w-[44px] !w-[44px] h-7 items-center bg-transparent border-transparent shadow-none hover:border-gm-blue dark:hover:border-light-blue',
            value ? 'text-inherit' : '!text-error dark:!text-error'
          )
        },
        input: {
          className: classNames(
            '!px-0.5 !py-1 text-xs !font-bold leading-4 relative top-[1px]',
            value ? 'text-inherit' : '!text-error dark:!text-error'
          )
        },
        trigger: {
          className: '!w-3 items-center !font-bold text-inherit'
        }
      }}
      onClick={event => event.stopPropagation()}
      onChange={event => onChange(event.value)}
    />
  </div>
)

const renderStateHeaderLabel = (header: string) => (
  <span className="block pt-3 pb-2 leading-4">{header}</span>
)

// Module-level helpers for RH file name encoding matching (shared between import form and dialog).
// The parsing/matching logic lives in utils/rh-encoding so it stays consistent
// everywhere encodings are compared.
const parseRHFileComponents = parseRHEncodingComponents

// Collect every selected ride height file that shares the same encoding as the
// representative file chosen for a state. This keeps the actual (raw) file names
// intact instead of reconstructing them from a hardcoded suffix list.
const collectRHFileNamesForState = (
  files: { name: string }[],
  representativeName: string
): string[] => {
  const repComponents = parseRHFileComponents(representativeName)
  if (!repComponents) return [representativeName]
  const repKey = repComponents.join('_')
  const matches = files
    .filter(file => {
      const components = parseRHFileComponents(file.name)
      return components !== null && components.join('_') === repKey
    })
    .map(file => file.name)
  return matches.length > 0 ? matches : [representativeName]
}

const AttitudeSets: FC = () => {
  const currentTeam = useRecoilValue(currentTeamState)
  const currentUser = useRecoilValue(currentUserState)
  const userCanMutate = canMutate(currentUser?.role?.roleId)
  const gmid = useRecoilValue(currentGMIDState)
  const [mode, setMode] = useState('')
  const [stateIndex, setStateIndex] = useState(0)
  const [showStateEditor, setShowStateEditor] = useState(false)
  const [{}, fetchAdmSeries] = useGetAdmApiSeries()
  const [{ loading: loadingAdmSequences, data: admSequencesData }, fetchAdmSequences] =
    useGetAdmSequencesForSeries()
  const [{ loading: loadingAdmTeamSequences, data: admTeamSequencesData }, fetchAdmTeamSequences] =
    useGetAdmSequencesForTeam()
  const [{ loading: loadingAdmPoints, data: admPointsData }, fetchAdmPoints] =
    useGetAdmSequencePoints()
  const [selectedAdmSeries, setSelectedAdmSeries] = useState<any>(null)
  const [{ loading: loadingAttitudeSets, data: attitudeSetsData }, fetchAttitudeSets] =
    useGetAttitudeSets()
  const [{}, deleteAttitudeSet] = useDeleteAttitudeSet()
  const [{ loading: creating }, createAttitudeSet] = useCreateAttitudeSet()
  const [{ loading: updating }, updateAttitudeSet] = useUpdateAttitudeSet()
  const [{ loading: uploadingCsv }, uploadAttitudeSetCsv] = useUploadAttitudeSetCsv() as any
  const [{ loading: creatingRHRepo }, createRHRepoSet] = useCreateRHRepoSet()
  const [{}, updateRHRepoSet] = useUpdateRHRepoSet()
  const [{}, fetchRHRepoSets] = useGetRHRepoSets()
  const [{ data: raceTeamData }, fetchRaceTeam] = useGetRaceTeam()
  const [{}, generateSasUrlUpload] = useGenerateBlobSasUrlUpload()
  const [setToShow, setSetToshow] = useState<AttitudeSet>()
  const [showStatesDetail, setShowStatesDetail] = useState(false)
  const [editableStateNames, setEditableStateNames] = useState<Record<number, string>>({})
  const [editableSpecValues, setEditableSpecValues] = useState<Record<number, number>>({})
  const [editableRadBlockerValues, setEditableRadBlockerValues] = useState<Record<number, number>>(
    {}
  )
  const [selectedSpeedType, setSelectedSpeedType] = useState('')
  const [dialogSpeedType, setDialogSpeedType] = useState('')
  const [viewDialogRHRepoSet, setViewDialogRHRepoSet] = useState<any>(null)
  const [dialogSelectedRHFiles, setDialogSelectedRHFiles] = useState<any[]>([])
  const [dialogRHFileMapping, setDialogRHFileMapping] = useState<Record<string, string>>({})
  const [dialogRhAeroTempRunId, setDialogRhAeroTempRunId] = useState<string | null>(null)
  const [isUploadingDialogRHFiles, setIsUploadingDialogRHFiles] = useState(false)
  const [dialogRHUploadProgress, setDialogRHUploadProgress] = useState<{
    phase: string
    percent: number
  }>({
    phase: '',
    percent: 0
  })
  const dialogRhFileInputRef = useRef<HTMLInputElement>(null)
  // Holds the in-flight dialog zip upload (compress + upload) so a Save & Close
  // that races the upload can wait for the zip to land in storage before the
  // server-side decompression is kicked off.
  const dialogUploadPromiseRef = useRef<Promise<void> | null>(null)
  // True while the dialog zip is still compressing/uploading. Used to decide
  // whether the decompression should show its blocking modal (upload finished)
  // or run silently in the background banner (user saved & closed early).
  const dialogUploadInFlightRef = useRef(false)

  const [globalAttitudeSet, setGlobalAttitudeSet] = useRecoilState(attitudeSetListState)
  const [aeroSequences, setSequence] = useState<any[]>([])
  const [attitudeSets, setAttitudeSets] = useState<AttitudeSet[]>([] as AttitudeSet[])
  const [currentSequence, setCurrentSequence] = useState<any>(null)
  const [sequenceStates, setSequenceStates] = useState<any[]>([] as any[])
  const [selectedRHFiles, setSelectedRHFiles] = useState<any[]>([])
  const [rideHeightFileMapping, setRideHeightFileMapping] = useState<Record<string, string>>({})
  const [rhAeroTempRunId, setRhAeroTempRunId] = useState<string | null>(null)
  const [isUploadingRHFiles, setIsUploadingRHFiles] = useState(false)
  const [isDecompressingRHFiles, setIsDecompressingRHFiles] = useState(false)
  const [decompressProgress, setDecompressProgress] = useState(0)
  const [uploadProgress, setUploadProgress] = useState<{
    total: number
    completed: number
    currentFile: string
  }>({ total: 0, completed: 0, currentFile: '' })
  const rhFileInputRef = useRef<HTMLInputElement>(null)
  const topSaveButtonRef = useRef<HTMLDivElement>(null)
  const [isTopSaveButtonVisible, setIsTopSaveButtonVisible] = useState(true)

  // CSV Upload state
  const [showCsvUploadDialog, setShowCsvUploadDialog] = useState(false)
  const [csvUploadName, setCsvUploadName] = useState('')
  const [csvUploadDescription, setCsvUploadDescription] = useState('')
  const [csvUploadFile, setCsvUploadFile] = useState<File | null>(null)
  const [csvUploadError, setCsvUploadError] = useState('')

  const [lazyAttitudeState, setLazyAttitudeState] = useState<LazyTableState>({
    first: 0,
    rows: 10,
    page: 1,
    sortField: 'name',
    sortOrder: -1,
    filters: defaultAttitudeSetFilters,
    sumlvl: ''
  })

  // Filter state columns based on team - hide Front Flap and Rear Flap for testing teams and NASCAR-Cup
  // Also hide SPEC and RAD_BLOCKER for non-NASCAR-Cup-28 teams
  const stateColumns = useMemo(() => {
    const shouldHideFlapColumns =
      NASCARTeams.includes(currentTeam) ||
      currentTeam?.toLowerCase().includes('testing') ||
      currentTeam?.toLowerCase().includes('test')

    const isNascar28 = NASCAR28Teams.includes(currentTeam)

    let filtered = baseStateColumns

    if (shouldHideFlapColumns) {
      filtered = filtered.filter(col => col.header !== 'Front Flap' && col.header !== 'Rear Flap')
    }

    if (!isNascar28) {
      filtered = filtered.filter(col => !col.nascar28Only)
    }

    return filtered
  }, [currentTeam])

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = useForm<AttitudeSet>()

  // Intersection Observer to track if top save button is visible
  useEffect(() => {
    if (!topSaveButtonRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsTopSaveButtonVisible(entry.isIntersecting)
      },
      {
        threshold: 0.1,
        rootMargin: '0px'
      }
    )

    observer.observe(topSaveButtonRef.current)

    return () => {
      observer.disconnect()
    }
  }, [topSaveButtonRef.current])

  // Fetch race team data to get blob storage info
  useEffect(() => {
    if (currentTeam) {
      fetchRaceTeam(currentTeam).catch(err => {
        console.error('Error fetching race team:', err)
      })
    }
  }, [currentTeam])

  useEffect(() => {
    try {
      if (attitudeSetsData) {
        const tmpList = attitudeSetsData.protoBytesList?.map((item: string) => {
          return protoService.deSerialize(item, 'attitudeset')
        })
        tmpList && setAttitudeSets(tmpList)
      } else {
        setAttitudeSets([])
      }
    } catch (error) {
      console.error('Error processing attitude sets data:', error)
      setAttitudeSets([])
      toast.error('Error processing imported attitude sets.')
    }
  }, [attitudeSetsData])

  // Fetch series list and team-resolved sequences
  useEffect(() => {
    fetchAdmSeries().catch(err => {
      console.error(err)
      toast.error('Error loading ADM series.')
    })
    fetchAdmTeamSequences().catch(err => {
      console.error(err)
      toast.error('Error loading ADM sequences for team.')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeam])

  // When an ADM series is selected manually, fetch sequences for that series
  useEffect(() => {
    if (selectedAdmSeries) {
      fetchAdmSequences(selectedAdmSeries.id).catch(err => {
        console.error(err)
        toast.error('Error loading ADM sequences.')
      })
    }
  }, [selectedAdmSeries])

  // When ADM sequences data returns (from team-resolved or series-selected), populate the dropdown
  useEffect(() => {
    const data = admSequencesData || admTeamSequencesData
    if (data) {
      const mapped = data.map((seq: any) => ({
        id: seq.id,
        description: seq.name,
        pointCount: seq.pointCount,
        tunnelLocation: seq.tunnelLocation?.name,
        duration: seq.duration
      }))
      setSequence(mapped)
    }
  }, [admSequencesData, admTeamSequencesData])

  // When ADM points data returns, populate the sequence states
  useEffect(() => {
    if (admPointsData) {
      const mapped = admPointsData.map((point: any) => ({
        order_number: point.pointOrder,
        name: `State_${point.pointOrder}`,
        front_height: point.frontHeight,
        rear_height: point.rearHeight,
        roll: point.roll,
        steer: point.steer,
        yaw: point.yaw,
        road_speed: point.roadSpeed,
        air_speed: point.airSpeed,
        input1: point.input1,
        input2: point.input2,
        input3: point.input3,
        input4: point.input4,
        tareOn: point.tareOn,
        sampleTimeSeconds: point.sampleTimeSeconds,
        dataOn: point.dataOn,
        atd: point.atd
      }))
      setSequenceStates(mapped)
    }
  }, [admPointsData])

  useEffect(() => {
    const query = ''
    fetchAttitudeSets(query).catch(err => {
      console.error(err)
      toast.error('Error loading imported sets.')
    })
  }, [lazyAttitudeState, currentTeam])

  // Auto-map ride height files to attitude states based on file names
  // Uses value-based matching to handle varying trailing-zero conventions in file encodings
  useEffect(() => {
    if (selectedRHFiles.length > 0 && sequenceStates.length > 0) {
      setRideHeightFileMapping(prevMapping => {
        const mapping: Record<string, string> = {}

        sequenceStates.forEach(state => {
          const stateName = state.name || `State_${state.order_number}`

          // Check if there's an existing mapping for this state name (preserve manual selections)
          if (prevMapping[stateName]) {
            mapping[stateName] = prevMapping[stateName]
            return
          }

          // Get attitude state values, defaulting to 0
          const frh = state.front_height || 0
          const rrh = state.rear_height || 0
          const roll = state.roll || 0
          const yaw = state.yaw || 0

          // Find file where all 4 encoding components match the state values
          const matchedFile = selectedRHFiles.find(file => {
            const components = parseRHFileComponents(file.name)
            if (!components) return false
            return (
              rhComponentMatchesValue(components[0], frh) &&
              rhComponentMatchesValue(components[1], rrh) &&
              rhComponentMatchesValue(components[2], roll) &&
              rhComponentMatchesValue(components[3], yaw)
            )
          })

          mapping[stateName] = matchedFile ? matchedFile.name : ''
        })

        return mapping
      })
    } else {
      setRideHeightFileMapping({})
    }
  }, [selectedRHFiles, sequenceStates])

  // Auto-map ride height files to dialog (existing attitude set) states
  useEffect(() => {
    if (dialogSelectedRHFiles.length > 0 && setToShow?.attitudeStates?.length > 0) {
      setDialogRHFileMapping(prevMapping => {
        const mapping: Record<string, string> = {}

        setToShow.attitudeStates.forEach((state, index) => {
          const stateName = state.name || `State_${index + 1}`

          if (prevMapping[stateName]) {
            mapping[stateName] = prevMapping[stateName]
            return
          }

          const frh = state.frh || 0
          const rrh = state.rrh || 0
          const roll = state.roll || 0
          const yaw = state.yaw || 0

          const matchedFile = dialogSelectedRHFiles.find(file => {
            const components = parseRHFileComponents(file.name)
            if (!components) return false
            return (
              rhComponentMatchesValue(components[0], frh) &&
              rhComponentMatchesValue(components[1], rrh) &&
              rhComponentMatchesValue(components[2], roll) &&
              rhComponentMatchesValue(components[3], yaw)
            )
          })

          mapping[stateName] = matchedFile ? matchedFile.name : ''
        })

        return mapping
      })
    } else if (dialogSelectedRHFiles.length === 0) {
      setDialogRHFileMapping({})
    }
  }, [dialogSelectedRHFiles, setToShow])

  const handleRHFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    // Generate or reuse temp run ID for staging files
    let tempRunId = rhAeroTempRunId
    if (!tempRunId) {
      tempRunId = `rh_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
      setRhAeroTempRunId(tempRunId)
      localStorage.setItem('aero_temp_run_id', tempRunId)
    }

    const fileList: any[] = []
    Array.from(files).forEach(file => {
      const filePath = (file as any).path || (file as any).webkitRelativePath || file.name
      fileList.push({ name: file.name, path: filePath })
    })

    // Set uploading state
    setIsUploadingRHFiles(true)
    setUploadProgress({ total: 3, completed: 0, currentFile: 'Compressing files...' })

    try {
      // Step 1: Read all files and compress into a single zip
      const fileData: Record<string, Uint8Array> = {}
      let totalUncompressed = 0
      for (const file of Array.from(files)) {
        const buffer = await file.arrayBuffer()
        fileData[file.name] = new Uint8Array(buffer)
        totalUncompressed += buffer.byteLength
      }

      console.log(
        `[RH Upload] Compressing ${files.length} files (${(totalUncompressed / 1024 / 1024).toFixed(
          1
        )} MB)...`
      )
      const zipped = await new Promise<Uint8Array>((resolve, reject) => {
        zip(fileData, { level: 6 }, (err, data) => (err ? reject(err) : resolve(data)))
      })
      const zipBlob = new Blob([
        (zipped.buffer as ArrayBuffer).slice(
          zipped.byteOffset,
          zipped.byteOffset + zipped.byteLength
        )
      ])
      const zipFile = new File([zipBlob], 'rh_files.zip', { type: 'application/zip' })
      const ratio = ((1 - zipFile.size / totalUncompressed) * 100).toFixed(0)
      console.log(
        `[RH Upload] Compressed to ${(zipFile.size / 1024 / 1024).toFixed(
          1
        )} MB (${ratio}% reduction)`
      )

      setUploadProgress({ total: 3, completed: 1, currentFile: 'Uploading compressed archive...' })

      // Step 2: Upload the single zip file
      await uploadFileDirectToBlob(
        generateSasUrlUpload,
        UploadTypes.RIDE_HEIGHT_FILE,
        'rh_files.zip',
        zipFile,
        tempRunId,
        gmid,
        (_filePath: string, percent: number) => {
          setUploadProgress(prev => ({
            ...prev,
            currentFile: `Uploading compressed archive... ${percent}%`
          }))
        }
      )

      setUploadProgress({ total: 3, completed: 2, currentFile: 'Upload complete!' })
      toast.success(`${files.length} file(s) compressed and uploaded successfully`)

      // Update selected files list
      setSelectedRHFiles(fileList)
    } catch (err) {
      console.error('Upload error:', err)
      toast.error('Error uploading files')
    } finally {
      setIsUploadingRHFiles(false)
    }

    // Reset input
    if (rhFileInputRef.current) {
      rhFileInputRef.current.value = ''
    }
  }

  const handleDialogRHFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    let tempRunId = dialogRhAeroTempRunId
    if (!tempRunId) {
      tempRunId = `rh_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
      setDialogRhAeroTempRunId(tempRunId)
      localStorage.setItem('aero_dialog_rh_temp_run_id', tempRunId)
    }

    // Pre-filter: only keep files that match at least one attitude state
    const states = setToShow?.attitudeStates || []
    const allFiles = Array.from(files)
    const matchedFiles = allFiles.filter(file => {
      const components = parseRHFileComponents(file.name)
      if (!components) return false
      return states.some(state => {
        const frh = state.frh || 0
        const rrh = state.rrh || 0
        const roll = state.roll || 0
        const yaw = state.yaw || 0
        return (
          rhComponentMatchesValue(components[0], frh) &&
          rhComponentMatchesValue(components[1], rrh) &&
          rhComponentMatchesValue(components[2], roll) &&
          rhComponentMatchesValue(components[3], yaw)
        )
      })
    })

    if (matchedFiles.length === 0) {
      toast.error(
        `No files in the selected directory match the attitude states (${allFiles.length} files checked).`
      )
      if (dialogRhFileInputRef.current) {
        dialogRhFileInputRef.current.value = ''
      }
      return
    }

    console.log(
      `[Dialog RH Upload] Matched ${matchedFiles.length} of ${allFiles.length} files to attitude states`
    )
    toast.success(
      `Matched ${matchedFiles.length} of ${allFiles.length} files — uploading only matching files.`
    )

    const fileList: any[] = matchedFiles.map(file => ({
      name: file.name,
      path: (file as any).webkitRelativePath || file.name
    }))

    // Populate the selected files immediately so the auto-map effect builds the
    // ride-height mapping right away. This way, if the user clicks "Save & Close"
    // while compression is still running, the mapping is already in place and the
    // RHRepoSet save (and the decompression that follows) is not skipped.
    setDialogSelectedRHFiles(fileList)

    setIsUploadingDialogRHFiles(true)
    setDialogRHUploadProgress({ phase: 'Reading files...', percent: 0 })

    const uploadId = `rh_upload_${Date.now()}`

    // Wrap read → compress → upload in a single promise so the decompression step
    // (kicked off on Save) can wait for the zip to actually land in storage, even
    // when Save & Close races the upload.
    const compressAndUploadPromise = (async () => {
      const fileData: Record<string, Uint8Array> = {}
      let totalUncompressed = 0
      for (let i = 0; i < matchedFiles.length; i++) {
        const buffer = await matchedFiles[i].arrayBuffer()
        fileData[matchedFiles[i].name] = new Uint8Array(buffer)
        totalUncompressed += buffer.byteLength
        setDialogRHUploadProgress({
          phase: 'Reading files...',
          percent: Math.round(((i + 1) / matchedFiles.length) * 100)
        })
      }

      setDialogRHUploadProgress({ phase: 'Compressing...', percent: 0 })
      const zipped = await new Promise<Uint8Array>((resolve, reject) => {
        zip(fileData, { level: 6 }, (err, data) => (err ? reject(err) : resolve(data)))
      })
      const zipBlob = new Blob([
        (zipped.buffer as ArrayBuffer).slice(
          zipped.byteOffset,
          zipped.byteOffset + zipped.byteLength
        )
      ])
      const zipFile = new File([zipBlob], 'rh_files.zip', { type: 'application/zip' })
      const ratio = ((1 - zipFile.size / totalUncompressed) * 100).toFixed(0)
      console.log(
        `[Dialog RH Upload] Compressing ${matchedFiles.length} files (${(
          totalUncompressed /
          1024 /
          1024
        ).toFixed(1)} MB) → ${(zipFile.size / 1024 / 1024).toFixed(1)} MB (${ratio}% reduction)`
      )

      // Now that the compressed size is known, show it in the progress banner.
      simUploadQueueStore.updateFileName(
        uploadId,
        `${matchedFiles.length} RH files (${(zipFile.size / 1024 / 1024).toFixed(1)} MB)`
      )

      // Compression finished — drop the modal overlay; the upload itself keeps
      // running in the background and is tracked by the progress banner.
      setIsUploadingDialogRHFiles(false)
      setDialogRHUploadProgress({ phase: '', percent: 0 })

      await uploadFileDirectToBlob(
        generateSasUrlUpload,
        UploadTypes.RIDE_HEIGHT_FILE,
        'rh_files.zip',
        zipFile,
        tempRunId,
        gmid,
        (_filePath: string, percent: number) => {
          simUploadQueueStore.updateProgress(uploadId, percent)
        }
      )
    })()

    // Track the in-flight upload so a Save & Close can await it before decompressing.
    dialogUploadPromiseRef.current = compressAndUploadPromise
    dialogUploadInFlightRef.current = true

    compressAndUploadPromise
      .catch(err => {
        console.error('Dialog RH upload error:', err)
        toast.error('Error uploading ride height files')
        setIsUploadingDialogRHFiles(false)
        setDialogRHUploadProgress({ phase: '', percent: 0 })
      })
      .finally(() => {
        dialogUploadInFlightRef.current = false
      })

    simUploadQueueStore.registerSimple(
      {
        runId: uploadId,
        runName: setToShow?.name || 'Attitude Set',
        fileName: `${matchedFiles.length} RH files`
      },
      compressAndUploadPromise.then(() => undefined)
    )

    toast.success(
      `${matchedFiles.length} file(s) uploading in the background — watch the progress bar at the bottom.`
    )

    if (dialogRhFileInputRef.current) {
      dialogRhFileInputRef.current.value = ''
    }
  }

  const handleScroll = () => {
    const ISRef = document.getElementById('importedSetsRef')
    ISRef.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
  }

  const handleItemSelected = attitudeSet => {
    try {
      if (!attitudeSet) {
        return
      }
      fetchAdmPoints(attitudeSet.id).catch(err => {
        console.error('Error loading ADM sequence points:', err)
        toast.error('Error loading sequence points from ADM API.')
        setSequenceStates([])
      })

      setCurrentSequence(attitudeSet)
      setMode('import')
      reset()
      setValue('name', attitudeSet.description)
      // Reset all ride height file related state when selecting new sequence
      setSelectedRHFiles([])
      setRideHeightFileMapping({})
      setEditableStateNames({})
      setSelectedSpeedType('')
      // Clear file input
      if (rhFileInputRef.current) {
        rhFileInputRef.current.value = ''
      }
      // Clear temp run ID
      if (rhAeroTempRunId) {
        setRhAeroTempRunId(null)
        localStorage.removeItem('aero_temp_run_id')
      }
      // Remove the immediate setSequenceStates call - let the useEffect handle it
      // when admPointsData updates from the API call
    } catch (error) {
      console.error('Error in handleItemSelected:', error)
      toast.error('Error selecting attitude set.')
      setSequenceStates([])
    }
  }

  const handleReimport = attitudeSet => {
    try {
      if (!attitudeSet) {
        return
      }
      fetchAdmPoints(attitudeSet.id).catch(err => {
        console.error('Error loading sequence points:', err)
        toast.error('Error loading attitude set.')
        setSequenceStates([])
      })
      setCurrentSequence(attitudeSet)
      setMode('reimport')
      reset()
      setValue('description', attitudeSet.description)
      setValue('name', attitudeSet.setName)
      // Reset all ride height file related state when re-importing
      setSelectedRHFiles([])
      setRideHeightFileMapping({})
      setEditableStateNames({})
      setSelectedSpeedType(attitudeSet.speedType || '')
      // Clear file input
      if (rhFileInputRef.current) {
        rhFileInputRef.current.value = ''
      }
      // Clear temp run ID
      if (rhAeroTempRunId) {
        setRhAeroTempRunId(null)
        localStorage.removeItem('aero_temp_run_id')
      }
      // Remove the immediate setSequenceStates call - let the useEffect handle it
      // when admPointsData updates from the API call
    } catch (error) {
      console.error('Error in handleReimport:', error)
      toast.error('Error selecting attitude set.')
      setSequenceStates([])
    }
  }

  const handleDeleteImportedSet = async attitudeSet => {
    if (!attitudeSet) {
      return
    }
    confirmDialog({
      message: `Are you sure you want to delete the attitude set: '${attitudeSet.name}'? This will only affect the imported set and not the original sequence.`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'reject',
      rejectClassName: 'bg-gray-500 text-white',
      accept: async () => {
        try {
          await deleteAttitudeSet({ id: attitudeSet._id } as any)
          const newAttitudeSets = attitudeSets.filter(item => item._id !== attitudeSet._id)
          setAttitudeSets(newAttitudeSets)
          toast.success('Attitude set deleted.')
        } catch (err) {
          console.error(err)
          toast.error('Error deleting attitude set.')
        }
      }
    })
  }

  const startRHDecompression = (savedId: string, tempRunId: string, runName: string) => {
    const decompressId = `rh_decompress_${Date.now()}`
    // If the zip upload is still in flight (e.g. the user hit Save & Close while
    // compression/upload was running), wait for it to finish before decompressing
    // so the server has the zip available in staging.
    const pendingUpload = dialogUploadPromiseRef.current
    const uploadStillRunning = dialogUploadInFlightRef.current

    // Only show the blocking modal when the upload has already completed. If the
    // user saved & closed early (upload still running), run the decompression
    // silently in the background via the progress banner so nothing pops up.
    if (!uploadStillRunning) {
      setIsDecompressingRHFiles(true)
      setDecompressProgress(0)
    }

    const finalizePromise = Promise.resolve(pendingUpload)
      .then(() =>
        finalizeRHRepoSetStreaming({ id: savedId, rhAeroTempRunId: tempRunId }, ({ percent }) => {
          setDecompressProgress(percent)
          simUploadQueueStore.updateProgress(decompressId, percent)
        })
      )
      .finally(() => {
        setIsDecompressingRHFiles(false)
        if (dialogUploadPromiseRef.current === pendingUpload) {
          dialogUploadPromiseRef.current = null
        }
      })
    simUploadQueueStore.registerDecompression(
      {
        runId: decompressId,
        runName,
        fileName: 'Decompressing ride height files'
      },
      finalizePromise
    )
  }

  const createDialogRHRepoSet = async (updatedStates: AttitudeState[]) => {
    if (Object.keys(dialogRHFileMapping).length === 0 || !setToShow) return null

    const rhrepoStates: RHRepo[] = []
    updatedStates.forEach((state, index) => {
      const stateName = state.name || `State_${index + 1}`
      const matchedFileName = dialogRHFileMapping[stateName]
      if (matchedFileName) {
        const encodingMatch = matchedFileName.match(/^([pm]\d+_[pm]\d+_[pm]\d+_[pm]\d+)/)
        const suspensionTypeEncoding = encodingMatch
          ? encodingMatch[1]
          : matchedFileName.split('_').slice(0, 4).join('_')
        // Pass the raw matched file names so the server stores them as-is
        const stateFileNames = collectRHFileNamesForState(dialogSelectedRHFiles, matchedFileName)
        const rhRepo = new RHRepo({
          name: stateName,
          description: '',
          path: '',
          suspensionTypeEncoding,
          fileRef: stateFileNames.map(fileName => new CFDFile({ file: fileName }))
        })
        rhrepoStates.push(rhRepo)
      }
    })

    if (rhrepoStates.length === 0) return null

    const existingId = viewDialogRHRepoSet?._id

    const rhRepoSet = new RHRepoSet({
      ...(existingId ? { _id: existingId } : {}),
      name: setToShow.name,
      description: setToShow.description,
      raceTeamId: currentTeam,
      version: currentTeam,
      suspensionTypeEncoding: '',
      rhrepoStates
    })

    try {
      const dataToPost = protoService.encodePayload(rhRepoSet, 'rhreposet')
      const params: any = { sequenceName: setToShow.name }
      if (dialogRhAeroTempRunId) {
        params.rhAeroTempRunId = dialogRhAeroTempRunId
      }
      // Use update when an existing RHRepoSet was loaded, create otherwise
      const result = existingId
        ? await updateRHRepoSet({ id: existingId, data: dataToPost, params } as any)
        : await createRHRepoSet({ data: dataToPost, params })

      if (result.data) {
        toast.success(
          `Ride height repository ${existingId ? 'updated' : 'created'} with ${
            rhrepoStates.length
          } state(s).`
        )
        const tempRunId = dialogRhAeroTempRunId
        if (dialogRhAeroTempRunId) {
          setDialogRhAeroTempRunId(null)
          localStorage.removeItem('aero_dialog_rh_temp_run_id')
        }
        let savedId = existingId
        if (result.data.protoBytesList?.length > 0) {
          const newRHRepoSet = protoService.deSerialize(result.data.protoBytesList[0], 'rhreposet')
          setViewDialogRHRepoSet(newRHRepoSet)
          savedId = savedId || newRHRepoSet._id
        }
        if (tempRunId && savedId) {
          startRHDecompression(savedId, tempRunId, setToShow.name)
        }
        setDialogSelectedRHFiles([])
        setDialogRHFileMapping({})
        return result.data
      }
    } catch (err) {
      console.error('Error saving dialog RHRepoSet:', err)
      toast.error('Error saving ride height files.')
    }
    return null
  }

  const handleSaveStateNames = async () => {
    if (!setToShow) return
    if (!dialogSpeedType) {
      toast.error('Select a speed unit before saving.')
      return
    }
    // Do NOT pass the proto instance to the constructor — copy scalar fields manually
    // to avoid setRepeatedWrapperField errors with nested proto objects.
    const updatedSet = new AttitudeSet()
    updatedSet._id = setToShow._id
    updatedSet.raceTeamId = setToShow.raceTeamId
    updatedSet.name = setToShow.name
    updatedSet.description = setToShow.description
    updatedSet.sequencerId = setToShow.sequencerId
    updatedSet.speedType = dialogSpeedType
    updatedSet.attitudeStates = setToShow.attitudeStates.map((original, index) => {
      const newState = new AttitudeState()
      newState.name = editableStateNames[index] ?? original.name
      newState.frh = original.frh
      newState.rrh = original.rrh
      newState.steer = original.steer
      newState.roll = original.roll
      newState.frontFlap = original.frontFlap
      newState.rearFlap = original.rearFlap
      newState.yaw = original.yaw
      newState.radius = original.radius
      newState.speed = original.speed
      newState.exhaust = original.exhaust
      newState.ccaId = original.ccaId
      newState.spec = editableSpecValues[index] ?? original.spec
      newState.radBlocker = editableRadBlockerValues[index] ?? original.radBlocker
      return newState
    })
    const dataToPost = protoService.encodePayload(updatedSet, 'attitudeset')
    try {
      await updateAttitudeSet({ id: setToShow._id, data: dataToPost } as any)
      setAttitudeSets(prev => prev.map(s => (s._id === setToShow._id ? updatedSet : s)))
      setSetToshow(updatedSet)
      toast.success('State names saved.')

      // Also save dialog RH repo if files were uploaded, passing the freshly-built
      // updatedSet.attitudeStates so RHRepo state names match the just-saved names.
      if (Object.keys(dialogRHFileMapping).length > 0) {
        await createDialogRHRepoSet(updatedSet.attitudeStates)
      }

      setShowStatesDetail(false)
    } catch (err) {
      console.error(err)
      toast.error('Error saving state names.')
    }
  }

  const handleRefresh = () => {
    const query = ''
    fetchAttitudeSets(query).catch(err => {
      console.error(err)
      toast.error('Error loading imported sets.')
    })
  }

  // Helper function to create RHRepoSet document for ride height files
  const createRHRepoSetDocument = async (name: string, description: string) => {
    if (Object.keys(rideHeightFileMapping).length === 0) {
      return null
    }

    // Build rhrepoStates array with minimal info - server will create full CFDFile objects
    const rhrepoStates: RHRepo[] = []

    sequenceStates.forEach(state => {
      const stateName = state.name || `State_${state.order_number}`
      const matchedFileName = rideHeightFileMapping[stateName]

      if (matchedFileName) {
        // Extract suspensionTypeEncoding directly from the matched file name
        // This ensures the encoding matches the actual file names in blob storage
        // e.g., "p175_p110_p000_m200_dom_RH.txt" -> "p175_p110_p000_m200"
        const encodingMatch = matchedFileName.match(/^([pm]\d+_[pm]\d+_[pm]\d+_[pm]\d+)/)
        const suspensionTypeEncoding = encodingMatch
          ? encodingMatch[1]
          : matchedFileName.split('_').slice(0, 4).join('_')

        // Collect the raw matched file names for this state and pass them through
        // so the server stores the actual file names instead of generating them
        const stateFileNames = collectRHFileNamesForState(selectedRHFiles, matchedFileName)

        // Create RHRepo object carrying the real file names in fileRef
        const rhRepo = new RHRepo({
          name: stateName,
          description: '',
          path: '', // Server will set this
          suspensionTypeEncoding: suspensionTypeEncoding,
          fileRef: stateFileNames.map(fileName => new CFDFile({ file: fileName }))
        })

        rhrepoStates.push(rhRepo)
      }
    })

    if (rhrepoStates.length === 0) {
      return null
    }

    // Create RHRepoSet object with minimal info
    const rhRepoSet = new RHRepoSet({
      name: name,
      description: description,
      raceTeamId: currentTeam,
      version: currentTeam,
      suspensionTypeEncoding: currentSequence?.suspensionTypeEncoding || '',
      rhrepoStates: rhrepoStates
    })

    // Serialize and post to server with staging info
    try {
      const dataToPost = protoService.encodePayload(rhRepoSet, 'rhreposet')

      // Pass rhAeroTempRunId and sequence name so server can move files from staging
      // If rhAeroTempRunId is missing, server will use fallback logic
      const params: any = {
        sequenceName: currentSequence?.description || name || 'sequence'
      }

      if (rhAeroTempRunId) {
        params.rhAeroTempRunId = rhAeroTempRunId
      }

      const result = await createRHRepoSet({
        data: dataToPost,
        params: params
      })

      if (result.data) {
        toast.success(`Ride height repository created with ${rhrepoStates.length} state(s).`)

        const tempRunId = rhAeroTempRunId
        // Clear temp run ID after successful creation
        if (rhAeroTempRunId) {
          setRhAeroTempRunId(null)
          localStorage.removeItem('aero_temp_run_id')
        }

        if (tempRunId && result.data.protoBytesList?.length > 0) {
          const newRHRepoSet = protoService.deSerialize(result.data.protoBytesList[0], 'rhreposet')
          if (newRHRepoSet?._id) {
            startRHDecompression(newRHRepoSet._id, tempRunId, name)
          }
        }

        return result.data
      }
    } catch (err) {
      console.error('Error creating RHRepoSet:', err)
      toast.error('Error saving ride height files.')
    }

    return null
  }

  const onSubmit: SubmitHandler<AttitudeSet> = async (formData: AttitudeSet) => {
    if (mode === 'import') {
      if (!selectedSpeedType) {
        toast.error('Select a speed unit before saving.')
        return
      }

      // Check if an attitude set with this name already exists
      const nameExists = attitudeSets.some(
        set => set.name.toLowerCase() === formData.name.toLowerCase()
      )

      if (nameExists) {
        toast.error('An attitude set with this name already exists. Please use a different name.')
        return
      }

      // create new attitude set
      const newAttitudeSet = new AttitudeSet(formData)
      newAttitudeSet.raceTeamId = currentTeam
      newAttitudeSet.sequencerId = currentSequence.id
      newAttitudeSet.speedType = selectedSpeedType
      newAttitudeSet.attitudeStates = sequenceStates.map(item => {
        const newState = new AttitudeState()
        // Use the custom name from the item (edited or auto-generated)
        newState.name = item.name || `${formData.name}_${item.order_number}`
        newState.exhaust = item.exhaust
        newState.frh = item.front_height
        newState.rrh = item.rear_height
        newState.roll = item.roll
        newState.steer = item.steer
        newState.frontFlap = item.front_flap
        newState.rearFlap = item.rear_flap
        newState.yaw = item.yaw
        newState.radius = item.radius
        newState.speed = item.air_speed
        newState.radius = item.radius || 0
        newState.exhaust = item.tunnel_state || 0
        // Convert to string - cca_id comes as number from SQL, but proto expects string
        newState.ccaId = item.cca_id != null ? String(item.cca_id) : ''
        newState.spec = item.spec || 0
        newState.radBlocker = item.rad_blocker || 0
        return newState
      })
      const dataToPost = protoService.encodePayload(newAttitudeSet, 'attitudeset')
      try {
        const res = await createAttitudeSet({ data: dataToPost })
        if (res.data.protoBytesList && res.data.protoBytesList.length > 0) {
          const savedAttitudeSet: AttitudeSet = protoService.deSerialize(
            res.data.protoBytesList[0],
            'attitudeset'
          )
          handleRefresh()
          setGlobalAttitudeSet([...globalAttitudeSet, savedAttitudeSet])
          toast.success('Attitude set created.')

          // Create RHRepoSet if ride height files were uploaded
          if (Object.keys(rideHeightFileMapping).length > 0) {
            await createRHRepoSetDocument(formData.name, currentSequence.description)
          }

          // Reset the form after successful import
          setMode('')
          setCurrentSequence(null)
          setSequenceStates([])
          setSelectedRHFiles([])
          setRideHeightFileMapping({})
          setEditableStateNames({})
          setSelectedSpeedType('')
          // Clear file input
          if (rhFileInputRef.current) {
            rhFileInputRef.current.value = ''
          }
          reset()
          // Scroll to imported sets section
          setTimeout(() => handleScroll(), 300)
        }
      } catch (err) {
        console.error(err)
        // Check if it's a name conflict error
        if (err?.response?.status === 409) {
          toast.error('An attitude set with this name already exists. Please use a different name.')
        } else {
          toast.error('Error creating attitude set.')
        }
      }
    } else if (mode === 'reimport') {
      if (!selectedSpeedType) {
        toast.error('Select a speed unit before saving.')
        return
      }

      // Check if another attitude set with this name already exists (excluding the current one)
      const nameExists = attitudeSets.some(
        set =>
          set.name.toLowerCase() === formData.name.toLowerCase() &&
          set._id !== currentSequence.rowId
      )

      if (nameExists) {
        toast.error('An attitude set with this name already exists. Please use a different name.')
        return
      }

      const newAttitudeSet = new AttitudeSet(formData)
      newAttitudeSet.raceTeamId = currentTeam
      newAttitudeSet.sequencerId = currentSequence.id
      newAttitudeSet._id = currentSequence.rowId
      newAttitudeSet.speedType = selectedSpeedType
      newAttitudeSet.attitudeStates = sequenceStates.map(item => {
        const newState = new AttitudeState()
        // Use the custom name from the item (edited or auto-generated)
        newState.name = item.name || `${formData.name}_${item.order_number}`
        newState.exhaust = item.exhaust
        newState.frh = item.front_height
        newState.rrh = item.rear_height
        newState.roll = item.roll
        newState.steer = item.steer
        newState.frontFlap = item.front_flap
        newState.rearFlap = item.rear_flap
        newState.yaw = item.yaw
        newState.radius = item.radius
        newState.speed = item.air_speed
        newState.radius = item.radius || 0
        newState.exhaust = item.tunnel_state || 0
        // Convert to string - cca_id comes as number from SQL, but proto expects string
        newState.ccaId = item.cca_id != null ? String(item.cca_id) : ''
        newState.spec = item.spec || 0
        newState.radBlocker = item.rad_blocker || 0
        return newState
      })
      const dataToPost = protoService.encodePayload(newAttitudeSet, 'attitudeset')
      try {
        const res = await updateAttitudeSet({
          id: currentSequence.rowId,
          data: dataToPost
        } as any)
        if (res.data.protoBytesList && res.data.protoBytesList.length > 0) {
          const savedAttitudeSet: AttitudeSet = protoService.deSerialize(
            res.data.protoBytesList[0],
            'attitudeset'
          )
          handleRefresh()
          setGlobalAttitudeSet([...globalAttitudeSet, savedAttitudeSet])
          toast.success('Attitude set updated.')

          // Create RHRepoSet if ride height files were uploaded
          if (Object.keys(rideHeightFileMapping).length > 0) {
            await createRHRepoSetDocument(formData.name, currentSequence.description)
          }

          // Reset the form after successful reimport
          setMode('')
          setCurrentSequence(null)
          setSequenceStates([])
          setSelectedRHFiles([])
          setRideHeightFileMapping({})
          setEditableStateNames({})
          setSelectedSpeedType('')
          // Clear file input
          if (rhFileInputRef.current) {
            rhFileInputRef.current.value = ''
          }
          reset()
          // Scroll to imported sets section
          setTimeout(() => handleScroll(), 300)
        }
      } catch (err) {
        console.error(err)
        // Check if it's a name conflict error
        if (err?.response?.status === 409) {
          toast.error('An attitude set with this name already exists. Please use a different name.')
        } else {
          toast.error('Error updating attitude set.')
        }
      }
    }
  }

  const getFormErrorMessage = name => {
    return errors[name] ? (
      <small className="error">{errors[name]?.message}</small>
    ) : (
      <small className="error">&nbsp;</small>
    )
  }

  const CSV_TEMPLATE_HEADER =
    'name,frh,rrh,steer,roll,yaw,frontFlap,rearFlap,radius,speed,exhaust,ccaId,spec,radBlocker'
  const CSV_TEMPLATE_EXAMPLE_ROWS = [
    'State_1,0.175,0.110,0,0,0,0,0,0,0,0,,1.0,1.0',
    'State_2,0.175,0.110,0,0.5,0,0,0,0,0,0,,2.0,3.0',
    'State_3,0.180,0.115,0,0,-3,0,0,5000,35,0,,3.0,6.0'
  ]

  const handleDownloadCsvTemplate = () => {
    const content = [CSV_TEMPLATE_HEADER, ...CSV_TEMPLATE_EXAMPLE_ROWS].join('\n')
    const blob = new Blob([content], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'attitude_set_template.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCsvUploadError('')
    const file = e.target.files?.[0]
    if (!file) {
      setCsvUploadFile(null)
      return
    }
    if (!file.name.endsWith('.csv')) {
      setCsvUploadError('Please select a .csv file.')
      setCsvUploadFile(null)
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setCsvUploadError('File size must not exceed 5 MB.')
      setCsvUploadFile(null)
      return
    }
    setCsvUploadFile(file)
    // Auto-fill name from file name
    const nameWithoutExt = file.name.replace(/\.csv$/i, '')
    setCsvUploadName(nameWithoutExt)
  }

  const validateAttitudeCsv = (content: string): string | null => {
    const lines = content.trim().split(/\r?\n/)
    if (lines.length < 2) {
      return 'CSV must contain a header row and at least one data row.'
    }
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
    const requiredCols = ['name', 'frh', 'rrh']
    const missing = requiredCols.filter(col => !headers.includes(col))
    if (missing.length > 0) {
      return `CSV is missing required columns: ${missing.join(', ')}. Required: name, frh, rrh.`
    }
    if (lines.length - 1 > 5000) {
      return 'CSV exceeds maximum of 5,000 data rows.'
    }
    // Validate a few numeric fields
    const frhIdx = headers.indexOf('frh')
    const rrhIdx = headers.indexOf('rrh')
    for (let i = 1; i < Math.min(lines.length, 6); i++) {
      const vals = lines[i].split(',').map(v => v.trim())
      if (frhIdx >= 0 && vals[frhIdx] && isNaN(parseFloat(vals[frhIdx]))) {
        return `Row ${i + 1}: "frh" value "${vals[frhIdx]}" is not a valid number.`
      }
      if (rrhIdx >= 0 && vals[rrhIdx] && isNaN(parseFloat(vals[rrhIdx]))) {
        return `Row ${i + 1}: "rrh" value "${vals[rrhIdx]}" is not a valid number.`
      }
    }
    return null
  }

  const handleCsvUploadSubmit = async () => {
    if (!csvUploadFile || !csvUploadName.trim()) {
      toast.error('Please fill in the name and select a CSV file.')
      return
    }

    setCsvUploadError('')

    const csvContent = await csvUploadFile.text()
    const validationError = validateAttitudeCsv(csvContent)
    if (validationError) {
      setCsvUploadError(validationError)
      return
    }

    try {
      const res = await uploadAttitudeSetCsv({
        name: csvUploadName.trim(),
        description: csvUploadDescription.trim(),
        csvContent
      })
      toast.success(`Attitude set "${csvUploadName}" uploaded successfully!`)
      setShowCsvUploadDialog(false)
      setCsvUploadFile(null)
      setCsvUploadName('')
      setCsvUploadDescription('')
      setCsvUploadError('')
      // Refresh imported sets
      handleRefresh()

      // Open the states detail view so the user can view states and upload RH files
      if (res.data?.protoBytesList?.length > 0) {
        const savedSet: AttitudeSet = protoService.deSerialize(
          res.data.protoBytesList[0],
          'attitudeset'
        )
        // Push the newly created set into the global list so selectors (e.g. Run
        // Creation) pick it up without a page refresh.
        setGlobalAttitudeSet([...globalAttitudeSet.filter(s => s._id !== savedSet._id), savedSet])
        setSetToshow(savedSet)
        setDialogSpeedType(savedSet.speedType || '')
        const names: Record<number, string> = {}
        savedSet.attitudeStates?.forEach((s: any, i: number) => {
          names[i] = s.name
        })
        setEditableStateNames(names)
        setViewDialogRHRepoSet(null)
        setDialogSelectedRHFiles([])
        setDialogRHFileMapping({})
        setDialogRhAeroTempRunId(null)
        setShowStatesDetail(true)
      }
      setTimeout(() => handleScroll(), 300)
    } catch (error: any) {
      console.error('CSV upload failed:', error)
      const status = error?.response?.status
      const msg = error?.response?.data || error?.message || 'Unknown error'
      if (status === 409) {
        setCsvUploadError(msg)
      } else {
        setCsvUploadError(`Upload failed: ${msg}`)
      }
    }
  }

  return (
    <>
      {/* Loading overlay for file uploads */}
      {isUploadingRHFiles && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center shadow-2xl min-w-[400px] max-w-[500px]">
            <LoadingIcon className="animate-spin h-16 w-16 fill-gm-blue mb-4" />
            <p className="text-xl font-semibold text-gray-800">Uploading Ride Height Files...</p>
            {/* Progress bar */}
            {uploadProgress.total > 0 && (
              <div className="w-full mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.round(
                        (uploadProgress.completed / uploadProgress.total) * 100
                      )}%`
                    }}
                  />
                </div>
              </div>
            )}
            {/* Current step status */}
            {uploadProgress.currentFile && (
              <p className="text-sm text-gray-600 mt-3 truncate w-full text-center">
                {uploadProgress.currentFile}
              </p>
            )}
          </div>
        </div>
      )}

      {isDecompressingRHFiles && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center shadow-2xl min-w-[400px] max-w-[500px]">
            <LoadingIcon className="animate-spin h-16 w-16 fill-gm-blue mb-4" />
            <p className="text-xl font-semibold text-gray-800">
              Decompressing Ride Height Files...
            </p>
            {/* Progress bar */}
            <div className="w-full mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${decompressProgress}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-2 text-center">{decompressProgress}%</p>
            </div>
            <p className="text-sm text-gray-600 mt-3 text-center">
              Extracting and saving your ride height files. This continues in the background if you
              close this dialog.
            </p>
          </div>
        </div>
      )}

      {/* Sticky Save Button - shows when form has content and top button is not visible */}
      {currentSequence && mode && !isTopSaveButtonVisible && (
        <div className="fixed bottom-8 right-8 z-40">
          <Button
            severity="info"
            className="!rounded-full !w-16 !h-16 !p-0 shadow-2xl hover:shadow-xl transition-shadow"
            type="submit"
            form="attitude-set-form"
            disabled={
              creating ||
              updating ||
              !currentSequence?.id ||
              sequenceStates.length === 0 ||
              !userCanMutate
            }
            title="Save Attitude Set"
            tooltip="Save Attitude Set"
            tooltipOptions={{ position: 'left' }}>
            {(creating || updating) && <LoadingIcon className="h-8 w-8 fill-white animate-spin" />}
            {!(creating || updating) && <SaveIcon className="fill-white w-8 h-8" />}
          </Button>
        </div>
      )}

      <div className="basis-0 grow flex flex-col items-stretch mt-6 min-w-full space-y-4">
        <div className="flex items-center justify-between pb-4">
          <h1 className="page-title">Attitude Sets</h1>
          <Button
            severity="secondary"
            className="cursor-pointer"
            disabled={uploadingCsv}
            onClick={() => {
              setCsvUploadName('')
              setCsvUploadDescription('')
              setCsvUploadFile(null)
              setCsvUploadError('')
              setShowCsvUploadDialog(true)
            }}>
            {uploadingCsv ? (
              <LoadingIcon className="h-4 w-4 fill-white animate-spin mr-2" />
            ) : (
              <UploadIcon className="h-4 w-4 fill-white stroke-transparent mr-2" />
            )}
            Upload CSV
          </Button>
        </div>
        <div className="flex text-gm-primary-90 text-xl pt-4 dark:text-light-blue">
          Sequencer Attitude Sets
        </div>
        <form
          id="attitude-set-form"
          onSubmit={handleSubmit(onSubmit)}
          className="w-full grid grid-cols-4 gap-x-2 gap-y-4 mt-8">
          <div className="flex items-center col-span-3 space-x-2">
            <Dropdown
              filter
              disabled={currentTeam === 'NBS-HFT'}
              loading={loadingAdmSequences || loadingAdmTeamSequences}
              filterBy="description"
              filterMatchMode="contains"
              showFilterClear
              onFilter={e => {}}
              value={currentSequence}
              emptyMessage="No AttitudeSets Found"
              pt={{
                ...DropdownStyle,
                root: {
                  ...DropdownStyle.root,
                  className: 'min-w-[30%] max-w-[50%]'
                }
              }}
              optionValue="id"
              optionLabel="description"
              useOptionAsValue
              options={aeroSequences}
              valueTemplate={option => {
                let value
                if (mode === 'new') {
                  value = 'New AttitudeSet'
                } else if (option) {
                  value = option.description
                } else {
                  value = (currentSequence && currentSequence.description) || 'Select Attitude Set'
                }
                return <div>{value}</div>
              }}
              itemTemplate={option => <div>{option && option.id ? option.description : ''}</div>}
              onChange={e => handleItemSelected(e.value)}
              placeholder="Select Attitude Set"
            />
            <div ref={topSaveButtonRef}>
              <Button
                severity="secondary"
                className="icon-button cursor-pointer"
                type="submit"
                disabled={creating || updating || !userCanMutate}
                title="Save Sequence"
                tooltip="Save Sequence"
                tooltipOptions={{ position: 'bottom' }}>
                {(creating || updating) && (
                  <LoadingIcon className="h-4 w-4 fill-white animate-spin" />
                )}
                {!(creating || updating) && <SaveIcon className="fill-white cursor-pointer" />}
              </Button>
            </div>
          </div>
          <div className="flex items-center col-span-1 space-x-2">
            <Button
              type="button"
              severity="secondary"
              onClick={() => {
                handleScroll()
              }}>
              View Imported Sets
            </Button>
            <Button
              type="button"
              severity="secondary"
              onClick={() => {
                window.open(
                  process.env.REACT_APP_ADM_URL || 'https://adm.gm-motorsports.com/',
                  '_blank'
                )
              }}>
              Go to Aero Sequencer
            </Button>
          </div>
          <div className="col-span-4">
            <div className="min-h-4"> </div>
            {currentSequence && (
              <>
                <section className="grid grid-cols-2 space-x-2">
                  <div className="field-box">
                    <Controller
                      name="name"
                      control={control}
                      disabled={creating || updating}
                      rules={{ required: 'Name is required.' }}
                      render={({ field, fieldState }) => (
                        <>
                          <label htmlFor={field.name} className="mb-2">
                            Name <span className="text-red-500">*</span>
                          </label>
                          <InputText
                            id={field.name}
                            {...field}
                            className={classNames({ error: fieldState.error })}
                          />
                          {getFormErrorMessage(field.name)}
                        </>
                      )}
                    />
                  </div>

                  <div className="field-box">
                    <Controller
                      name="description"
                      control={control}
                      disabled={creating || updating}
                      render={({ field, fieldState }) => (
                        <>
                          <label htmlFor={field.name} className="mb-2">
                            Description
                          </label>
                          <InputText
                            id={field.name}
                            {...field}
                            className={classNames({ error: fieldState.error })}
                          />
                          {getFormErrorMessage(field.name)}
                        </>
                      )}
                    />
                  </div>
                </section>

                <section className="col-span-2 space-y-4">
                  <div className="field-box">
                    <label className="mb-2 font-semibold">Ride Height Files (Optional)</label>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Select the root directory that directly contains the Ride Height files. Do not
                      select a parent folder containing multiple repositories or unrelated
                      subfolders. Example: <code>MyVehicle_RH/</code> should contain the RH files
                      and folders immediately within it.
                    </p>
                    <input
                      ref={rhFileInputRef}
                      type="file"
                      multiple
                      {...({ webkitdirectory: '', directory: '' } as any)}
                      className="hidden"
                      onChange={handleRHFileSelection}
                      disabled={
                        creating || updating || !currentSequence?.id || sequenceStates.length === 0
                      }
                    />
                    <Button
                      type="button"
                      severity="secondary"
                      onClick={() => rhFileInputRef.current?.click()}
                      disabled={
                        creating || updating || !currentSequence?.id || sequenceStates.length === 0
                      }
                      className={classNames('w-fit', {
                        'opacity-50 cursor-not-allowed':
                          creating ||
                          updating ||
                          !currentSequence?.id ||
                          sequenceStates.length === 0
                      })}>
                      {selectedRHFiles.length > 0
                        ? 'Files uploaded - Click to change'
                        : 'Select Ride Height Directory'}
                    </Button>
                  </div>
                </section>

                <section className="col-span-2 flex flex-col items-stretch mt-6">
                  <div className="font-semibold">States:</div>
                  <DataTable
                    value={sequenceStates}
                    scrollable
                    scrollHeight="650px"
                    loading={loadingAdmPoints}
                    dataKey="order_number"
                    filterDisplay="row"
                    stripedRows
                    sortField="order_number"
                    pt={{
                      bodyRow: {
                        className: 'table-row'
                      }
                    }}>
                    {stateColumns.map(item => {
                      const columnProps: any = {
                        key: item.field,
                        field: item.field,
                        header: renderStateHeaderLabel(item.header),
                        showFilterMenu: false,
                        sortable: item.sortable,
                        headerStyle: item.headerStyle,
                        headerClassName: classNames(item.headerClassName, {
                          'w-[64px] max-w-[64px] !px-1': item.field === 'air_speed'
                        }),
                        bodyStyle: item.bodyStyle,
                        bodyClassName: item.bodyClassName
                      }

                      if (item.field === 'air_speed') {
                        columnProps.header = renderSpeedTypeHeader(
                          selectedSpeedType,
                          setSelectedSpeedType,
                          creating || updating
                        )
                      }

                      // Add inline editing for State Name column
                      if (item.editable) {
                        columnProps.body = (rowData, options) => {
                          return (
                            <InputText
                              type="text"
                              value={rowData.name || ''}
                              onChange={e => {
                                const oldStateName = rowData.name || `State_${rowData.order_number}`
                                const newStateName = e.target.value

                                const updatedStates = [...sequenceStates]
                                updatedStates[options.rowIndex].name = newStateName
                                setSequenceStates(updatedStates)

                                // Update the ride height file mapping to use the new state name
                                if (
                                  oldStateName !== newStateName &&
                                  rideHeightFileMapping[oldStateName]
                                ) {
                                  const newMapping = { ...rideHeightFileMapping }
                                  newMapping[newStateName] = newMapping[oldStateName]
                                  delete newMapping[oldStateName]
                                  setRideHeightFileMapping(newMapping)
                                }
                              }}
                              className="w-full"
                              placeholder="Enter state name"
                            />
                          )
                        }
                      } else if (item.isRideHeightColumn) {
                        // Special handling for ride height file column - dropdown
                        columnProps.body = (rowData, options) => {
                          const stateName = rowData.name || `State_${rowData.order_number}`
                          const matchedFileName = rideHeightFileMapping[stateName] || ''
                          const selectedFile = selectedRHFiles.find(f => f.name === matchedFileName)

                          // Helper function to extract encoding from file name
                          const extractEncoding = (fileName: string) => {
                            const encodingMatch = fileName.match(
                              /([pm]\d{3,4}_[pm]\d{3,4}_[pm]\d{3,4}_[pm]\d{3,4})/
                            )
                            return encodingMatch ? encodingMatch[1] : null
                          }

                          // Helper function to format file name like the modal does (name/encoding)
                          const formatFileName = (fileName: string) => {
                            if (!fileName) return ''
                            const encoding = extractEncoding(fileName)
                            if (encoding) {
                              // Get the part before the encoding as the "name"
                              const nameMatch = fileName.match(
                                /^(.+?)_([pm]\d{3,4}_[pm]\d{3,4}_[pm]\d{3,4}_[pm]\d{3,4})/
                              )
                              const baseName = nameMatch ? nameMatch[1] : fileName.split('_')[0]
                              return `${baseName}/${encoding}`
                            }
                            return fileName
                          }

                          // Deduplicate files by encoding pattern - only show one file per unique encoding
                          const deduplicatedFiles = selectedRHFiles.reduce(
                            (acc, file) => {
                              const encoding = extractEncoding(file.name)
                              if (
                                encoding &&
                                !acc.some(f => extractEncoding(f.name) === encoding)
                              ) {
                                acc.push(file)
                              }
                              return acc
                            },
                            [] as typeof selectedRHFiles
                          )

                          return (
                            <Dropdown
                              value={selectedFile}
                              disabled={creating || updating || selectedRHFiles.length === 0}
                              placeholder={
                                selectedRHFiles.length === 0 ? 'No files uploaded' : 'Select file'
                              }
                              pt={DropdownStyle}
                              options={deduplicatedFiles}
                              optionLabel="name"
                              filter
                              filterBy="name"
                              filterMatchMode="contains"
                              showFilterClear
                              emptyMessage="No files available"
                              onChange={e => {
                                // Update mapping when user manually selects a file
                                const newMapping = { ...rideHeightFileMapping }
                                newMapping[stateName] = e.value?.name || ''
                                setRideHeightFileMapping(newMapping)
                              }}
                              valueTemplate={option => {
                                if (!option)
                                  return <span className="text-gray-400">No file selected</span>
                                const formatted = formatFileName(option.name)
                                return <span title={option.name}>{formatted}</span>
                              }}
                              itemTemplate={option => (
                                <div className="text-sm" title={option.name}>
                                  {formatFileName(option.name)}
                                </div>
                              )}
                            />
                          )
                        }
                      } else if (item.body) {
                        // Use custom body renderer if provided
                        columnProps.body = item.body
                      }

                      return <Column {...columnProps} />
                    })}
                    <Column
                      header={() => {
                        return <div></div>
                      }}
                      body={(rowData: any, options: ColumnBodyOptions) => {
                        return (
                          <div className="flex items-center space-x-1">
                            <IconButton
                              clear
                              icon={
                                <EditIcon className="h-4 w-4 fill-gm-blue stroke-transparent" />
                              }
                              onClick={() => {
                                setStateIndex(options.rowIndex)
                                setShowStateEditor(true)
                              }}></IconButton>
                            <IconButton
                              clear
                              icon={
                                <DeleteIcon className="h-4 w-4 fill-error stroke-transparent" />
                              }
                              onClick={() => {
                                const rowName = rowData.order_number
                                confirmDialog({
                                  message: `Are you sure you want to delete the state: '${rowName}'?`,
                                  header: 'Confirmation',
                                  icon: 'pi pi-exclamation-triangle',
                                  defaultFocus: 'reject',
                                  rejectClassName: 'bg-gray-500 text-white',
                                  accept: () => {
                                    const newStates = sequenceStates.filter(item => {
                                      return item.order_number !== rowName
                                    })
                                    setSequenceStates(newStates)
                                  }
                                })
                              }}></IconButton>
                          </div>
                        )
                      }}
                      headerClassName="one-col-editor-header editor-header"
                      pt={{
                        headerContent: {
                          className: 'pr-0 min-w-full'
                        },
                        headerTitle: {
                          className: 'pr-0 w-full flex items-center'
                        }
                      }}
                      bodyStyle={{ textAlign: 'center', padding: '12px 8px' }}></Column>
                  </DataTable>
                </section>
              </>
            )}
          </div>
        </form>

        {showStateEditor && (
          <AttitudeSetStateEditor
            stateIndex={stateIndex}
            states={sequenceStates}
            currentTeam={currentTeam}
            onConfirm={newState => {
              let newStates
              newStates = sequenceStates.map((item, index) => {
                if (index === stateIndex) {
                  newState.order_number = item.order_number
                  return newState
                } else {
                  return { ...item }
                }
              })
              setSequenceStates(newStates)
              setShowStateEditor(false)
            }}
            onCancel={() => {
              setShowStateEditor(false)
            }}
          />
        )}

        <div className="flex text-gm-primary-90 text-xl pt-4 dark:text-light-blue">
          Imported Sets
        </div>
        <section className="col-span-2 flex flex-col items-stretch">
          <DataTable
            id="importedSetsRef"
            value={attitudeSets}
            scrollable
            scrollHeight="650px"
            loading={loadingAttitudeSets}
            dataKey="_id"
            filterDisplay="row"
            stripedRows
            sortField="name"
            pt={{
              bodyRow: {
                className: 'table-row'
              }
            }}>
            {setColumns.map(item => {
              return (
                <Column
                  key={item.field}
                  field={item.field}
                  header={item.header}
                  showFilterMenu={false}
                  sortable={item.sortable}
                  headerStyle={item.headerStyle}
                  headerClassName={item.headerClassName}
                  bodyStyle={item.bodyStyle}
                  bodyClassName={item.bodyClassName}></Column>
              )
            })}
            <Column
              header="Actions"
              body={(rowData: any, options: ColumnBodyOptions) => {
                return (
                  <div className="flex items-center space-x-1">
                    <IconButton
                      clear
                      icon={<EyeIcon className="h-4 w-4 fill-gm-blue stroke-transparent" />}
                      title="Edit/View Attitude States"
                      onClick={() => {
                        setSetToshow(rowData)
                        const names: Record<number, string> = {}
                        rowData.attitudeStates?.forEach((s: any, i: number) => {
                          names[i] = s.name
                        })
                        setEditableStateNames(names)
                        const specs: Record<number, number> = {}
                        const radBlockers: Record<number, number> = {}
                        rowData.attitudeStates?.forEach((s: any, i: number) => {
                          specs[i] = s.spec || 0
                          radBlockers[i] = s.radBlocker || 0
                        })
                        setEditableSpecValues(specs)
                        setEditableRadBlockerValues(radBlockers)
                        setDialogSpeedType(rowData.speedType || '')
                        // Reset dialog RH upload state
                        setViewDialogRHRepoSet(null)
                        setDialogSelectedRHFiles([])
                        setDialogRHFileMapping({})
                        setDialogRhAeroTempRunId(null)
                        // Fetch matching RHRepoSet to show existing ride height file info
                        const encodedQuery = encodeURIComponent(
                          JSON.stringify({ name: rowData.name })
                        )
                        fetchRHRepoSets(`query=${encodedQuery}`)
                          .then((result: any) => {
                            if (result?.data?.protoBytesList?.length > 0) {
                              const rhRepoSet = protoService.deSerialize(
                                result.data.protoBytesList[0],
                                'rhreposet'
                              )
                              setViewDialogRHRepoSet(rhRepoSet)
                            }
                          })
                          .catch((err: any) => {
                            console.error('Error fetching RHRepoSet for dialog:', err)
                          })
                        setShowStatesDetail(true)
                      }}></IconButton>
                    <IconButton
                      clear
                      icon={<RefreshIcon className="h-4 w-4 fill-gm-blue stroke-transparent" />}
                      title="Re-Import Attitude Set"
                      onClick={() => {
                        handleReimport({
                          rowId: rowData._id,
                          id: rowData.sequencerId,
                          description: rowData.description,
                          setName: rowData.name,
                          speedType: rowData.speedType
                        })
                      }}></IconButton>
                    <IconButton
                      clear
                      icon={<DeleteIcon className="h-4 w-4 fill-error stroke-transparent" />}
                      title="Remove Imported Attitude Set"
                      onClick={() => {
                        handleDeleteImportedSet(rowData)
                      }}></IconButton>
                  </div>
                )
              }}
              headerClassName="one-col-editor-header editor-header"
              pt={{
                headerContent: {
                  className: 'pr-0 min-w-full'
                },
                headerTitle: {
                  className: 'pr-0 w-full flex items-center'
                }
              }}
              bodyStyle={{ textAlign: 'center', padding: '12px 8px' }}></Column>
          </DataTable>
        </section>
        <Dialog
          header={`${setToShow?.name} - Edit/View States`}
          visible={showStatesDetail}
          closeOnEscape
          pt={{
            root: {
              className: 'w-[80vw]'
            },
            mask: {
              className: 'bg-blue-backdrop dark:bg-neutral-80'
            },
            content: {
              className: 'max-h-[80vh] overflow-y-auto'
            }
          }}
          onHide={() => {
            if (!showStatesDetail) return
            setShowStatesDetail(false)
          }}>
          <div className="flex flex-col items-stretch">
            <div className="font-semibold mb-4">States:</div>
            <DataTable
              value={setToShow?.attitudeStates?.map((state, index) => {
                const stateName = state.name || `State_${index + 1}`
                const matchingRHRepo = viewDialogRHRepoSet?.rhrepoStates?.find(
                  (r: any) => r.name === stateName
                )
                const dialogMappedFile = dialogRHFileMapping[stateName]
                return {
                  order_number: index + 1,
                  name: state.name,
                  rideHeightFile: dialogMappedFile || matchingRHRepo?.suspensionTypeEncoding || '',
                  cca_id: state.ccaId,
                  front_height: state.frh,
                  rear_height: state.rrh,
                  steer: state.steer,
                  roll: state.roll,
                  front_flap: state.frontFlap,
                  rear_flap: state.rearFlap,
                  yaw: state.yaw,
                  radius: state.radius,
                  air_speed: state.speed,
                  tunnel_state: state.exhaust,
                  spec: state.spec,
                  rad_blocker: state.radBlocker
                }
              })}
              scrollable
              scrollHeight="500px"
              dataKey="order_number"
              stripedRows
              sortField="order_number"
              pt={{
                bodyRow: {
                  className: 'table-row'
                },
                thead: {
                  className: 'dark:bg-neutral-90 dark:border-neutral-70'
                }
              }}>
              {stateColumns.map(item => {
                return (
                  <Column
                    key={item.field}
                    field={item.field}
                    header={
                      item.field === 'air_speed'
                        ? renderSpeedTypeHeader(
                            dialogSpeedType,
                            setDialogSpeedType,
                            updating || creatingRHRepo
                          )
                        : renderStateHeaderLabel(item.header)
                    }
                    showFilterMenu={false}
                    sortable={item.sortable}
                    headerStyle={item.headerStyle}
                    headerClassName={classNames(
                      item.headerClassName,
                      'dark:bg-neutral-90 dark:text-white dark:border-neutral-70',
                      {
                        'w-[64px] max-w-[64px] !px-1': item.field === 'air_speed'
                      }
                    )}
                    bodyStyle={item.bodyStyle}
                    bodyClassName={item.bodyClassName}
                    body={
                      item.field === 'name'
                        ? rowData => (
                            <InputText
                              value={editableStateNames[rowData.order_number - 1] ?? rowData.name}
                              onChange={e =>
                                setEditableStateNames(prev => ({
                                  ...prev,
                                  [rowData.order_number - 1]: e.target.value
                                }))
                              }
                              className="w-full p-1 text-sm border border-gray-300 rounded dark:bg-neutral-80 dark:text-white dark:border-neutral-60"
                            />
                          )
                        : item.field === 'spec'
                          ? rowData => (
                              <input
                                type="number"
                                step="0.1"
                                value={
                                  editableSpecValues[rowData.order_number - 1] ?? rowData.spec ?? 0
                                }
                                onChange={e => {
                                  const val = parseFloat(parseFloat(e.target.value).toFixed(1)) || 0
                                  setEditableSpecValues(prev => ({
                                    ...prev,
                                    [rowData.order_number - 1]: val
                                  }))
                                }}
                                className="w-full p-1 text-sm border border-gray-300 rounded dark:bg-neutral-80 dark:text-white dark:border-neutral-60"
                              />
                            )
                          : item.field === 'rad_blocker'
                            ? rowData => (
                                <input
                                  type="number"
                                  step="0.1"
                                  value={
                                    editableRadBlockerValues[rowData.order_number - 1] ??
                                    rowData.rad_blocker ??
                                    0
                                  }
                                  onChange={e => {
                                    const val =
                                      parseFloat(parseFloat(e.target.value).toFixed(1)) || 0
                                    setEditableRadBlockerValues(prev => ({
                                      ...prev,
                                      [rowData.order_number - 1]: val
                                    }))
                                  }}
                                  className="w-full p-1 text-sm border border-gray-300 rounded dark:bg-neutral-80 dark:text-white dark:border-neutral-60"
                                />
                              )
                            : item.isRideHeightColumn
                              ? rowData => {
                                  const stateName = rowData.name || `State_${rowData.order_number}`
                                  const extractEncoding = (fileName: string) => {
                                    const m = fileName.match(
                                      /([pm]\d{3,4}_[pm]\d{3,4}_[pm]\d{3,4}_[pm]\d{3,4})/
                                    )
                                    return m ? m[1] : null
                                  }
                                  const formatFileName = (fileName: string) => {
                                    if (!fileName) return ''
                                    const encoding = extractEncoding(fileName)
                                    if (encoding) {
                                      const nm = fileName.match(
                                        /^(.+?)_([pm]\d{3,4}_[pm]\d{3,4}_[pm]\d{3,4}_[pm]\d{3,4})/
                                      )
                                      const baseName = nm ? nm[1] : fileName.split('_')[0]
                                      return `${baseName}/${encoding}`
                                    }
                                    return fileName
                                  }
                                  if (dialogSelectedRHFiles.length > 0) {
                                    const matchedFileName = dialogRHFileMapping[stateName] || ''
                                    const selectedFile = dialogSelectedRHFiles.find(
                                      f => f.name === matchedFileName
                                    )
                                    const deduplicatedFiles = dialogSelectedRHFiles.reduce(
                                      (acc, file) => {
                                        const enc = extractEncoding(file.name)
                                        if (enc && !acc.some(f => extractEncoding(f.name) === enc))
                                          acc.push(file)
                                        return acc
                                      },
                                      [] as typeof dialogSelectedRHFiles
                                    )
                                    return (
                                      <Dropdown
                                        value={selectedFile}
                                        placeholder="Select file"
                                        pt={DropdownStyle}
                                        options={deduplicatedFiles}
                                        optionLabel="name"
                                        filter
                                        filterBy="name"
                                        filterMatchMode="contains"
                                        showFilterClear
                                        emptyMessage="No files available"
                                        onChange={e => {
                                          setDialogRHFileMapping(prev => ({
                                            ...prev,
                                            [stateName]: e.value?.name || ''
                                          }))
                                        }}
                                        valueTemplate={option => {
                                          if (!option)
                                            return (
                                              <span className="text-gray-400">
                                                No file selected
                                              </span>
                                            )
                                          return (
                                            <span title={option.name}>
                                              {formatFileName(option.name)}
                                            </span>
                                          )
                                        }}
                                        itemTemplate={option => (
                                          <div className="text-sm" title={option.name}>
                                            {formatFileName(option.name)}
                                          </div>
                                        )}
                                      />
                                    )
                                  }
                                  return (
                                    <span className="text-sm" title={rowData.rideHeightFile}>
                                      {rowData.rideHeightFile || '—'}
                                    </span>
                                  )
                                }
                              : item.body
                    }></Column>
                )
              })}
            </DataTable>

            {/* Ride height repo upload section */}
            <div className="mt-6 border-t pt-4 space-y-2">
              <label className="font-semibold">
                {viewDialogRHRepoSet
                  ? 'Update Ride Height Repository'
                  : 'Add Ride Height Repository'}
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {viewDialogRHRepoSet
                  ? 'Upload a new ride height directory to replace the existing repository for this attitude set.'
                  : 'Upload a ride height directory to associate a ride height repository with this attitude set.'}
              </p>
              <input
                ref={dialogRhFileInputRef}
                type="file"
                multiple
                {...({ webkitdirectory: '', directory: '' } as any)}
                className="hidden"
                onChange={handleDialogRHFileSelection}
                disabled={isUploadingDialogRHFiles}
              />
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-3">
                  <Button
                    type="button"
                    severity="secondary"
                    onClick={() => dialogRhFileInputRef.current?.click()}
                    disabled={isUploadingDialogRHFiles}
                    loading={isUploadingDialogRHFiles}>
                    {isUploadingDialogRHFiles
                      ? `${dialogRHUploadProgress.phase} ${dialogRHUploadProgress.percent}%`
                      : dialogSelectedRHFiles.length > 0
                        ? 'Files uploading in background — click to change'
                        : 'Select Ride Height Directory'}
                  </Button>
                  {dialogSelectedRHFiles.length > 0 && !isUploadingDialogRHFiles && (
                    <span className="text-sm text-green-600 dark:text-green-400">
                      {Object.values(dialogRHFileMapping).filter(v => v).length} /{' '}
                      {setToShow?.attitudeStates?.length ?? 0} states matched
                    </span>
                  )}
                </div>
                {isUploadingDialogRHFiles && (
                  <div className="w-full max-w-sm">
                    <div className="w-full bg-gray-200 dark:bg-neutral-70 rounded-full h-2.5">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${dialogRHUploadProgress.percent}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {dialogRHUploadProgress.phase} {dialogRHUploadProgress.percent}%
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Button
                label="Save & Close"
                loading={updating || creatingRHRepo}
                onClick={handleSaveStateNames}
                className="p-button-primary"
              />
            </div>
          </div>
        </Dialog>

        {/* CSV Upload Dialog */}
        <Dialog
          header="Upload Attitude Set CSV"
          visible={showCsvUploadDialog}
          closeOnEscape
          pt={{
            root: {
              className: 'w-[550px]'
            },
            mask: {
              className: 'bg-blue-backdrop dark:bg-neutral-80'
            }
          }}
          onHide={() => {
            if (!showCsvUploadDialog) return
            setShowCsvUploadDialog(false)
          }}
          footer={
            <div className="flex justify-end space-x-2">
              <Button severity="secondary" outlined onClick={() => setShowCsvUploadDialog(false)}>
                Cancel
              </Button>
              <Button
                icon={
                  uploadingCsv ? (
                    <LoadingIcon className="h-4 w-4 fill-white animate-spin mr-2" />
                  ) : (
                    <UploadIcon className="fill-white w-4 h-4 mr-2" />
                  )
                }
                severity="secondary"
                disabled={uploadingCsv || !csvUploadName.trim() || !csvUploadFile}
                onClick={handleCsvUploadSubmit}>
                {uploadingCsv ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          }>
          <div className="flex flex-col space-y-4 pt-2">
            <div className="flex flex-col space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  CSV File <span className="text-red-500">*</span>
                </label>
                <Button
                  type="button"
                  severity="info"
                  className="!py-2 !px-3 !text-sm"
                  onClick={handleDownloadCsvTemplate}>
                  <DownloadIcon className="h-4 w-4 fill-white stroke-transparent mr-1" />
                  Download Template
                </Button>
              </div>
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvFileChange}
                className="block w-full text-sm text-gray-700 dark:text-gray-300
                  file:mr-4 file:py-2 file:px-4
                  file:rounded file:border-0
                  file:text-sm file:font-medium
                  file:bg-gray-100 file:text-gray-700
                  dark:file:bg-neutral-70 dark:file:text-gray-200
                  hover:file:bg-gray-200 dark:hover:file:bg-neutral-60
                  cursor-pointer"
              />
              <span className="text-xs text-gray-500">
                Required columns: name, frh, rrh. Optional: steer, roll, yaw, frontFlap, rearFlap,
                radius, speed, exhaust, ccaId
              </span>
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Name <span className="text-red-500">*</span>
              </label>
              <InputText
                value={csvUploadName}
                onChange={e => setCsvUploadName(e.target.value)}
                placeholder="e.g. NBS-HFT-Speedway"
                className="w-full"
              />
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Description (optional)
              </label>
              <InputText
                value={csvUploadDescription}
                onChange={e => setCsvUploadDescription(e.target.value)}
                placeholder="e.g. 2028 Speedway sweep states"
                className="w-full"
              />
            </div>
            {csvUploadError && (
              <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2 whitespace-pre-wrap">
                {csvUploadError}
              </div>
            )}
          </div>
        </Dialog>
      </div>
    </>
  )
}

export default AttitudeSets
